import { useState } from "react";
import Skeleton from "react-loading-skeleton";
import { useNavigate, useParams } from "react-router-dom";
import { BackButton } from "../../components/buttons/backButton";
import { Button } from "../../components/buttons/button";
import { GuestCard } from "../../components/cards/guestCard";
import { Icon } from "../../components/icon/icon";
import { SwitchInput } from "../../components/inputs/switch";
import { ConfirmDeleteModal } from "../../components/modal/app_modals/confirmDeleteModal";
import { DiscountModal } from "../../components/modal/app_modals/discountModal";
import { Pill } from "../../components/pill";
import { ProgressBar } from "../../components/progressBar";
import { TableComponent } from "../../components/table";
import { MiniHeader } from "../../components/typography/miniHeader";
import { BgWrapper } from "../../components/wrapper/bgWrapper";
import { useModalControl } from "../../customeHooks/useModalControl";
import {
  useDeleteDiscountMutation,
  useFetchDiscountUsageLogsQuery,
  useGetDiscountQuery,
  useGetEventsQuery,
  useToggleAssignEventDiscountMutation,
} from "../../redux/features/event/event.api";
import { useAppSelector } from "../../redux/hooks";
import {
  currencyFlags,
  currencyShorts,
  SKELETON_BASE_COLOR,
  SKELETON_HIGHLIGHT_COLOR,
} from "../../utils/constants";
import { formatToCurrency, getTimeAgo, getTimeToGo } from "../../utils/helpers";
import { QueryParams } from "../../utils/types";

export const SingleDiscount = () => {
  const [logsQuery, setLogsQuery] = useState<QueryParams>({
    page: 1,
    limit: 10,
  });

  const { profile } = useAppSelector((state) => state.auth);

  const { discountCode } = useParams();

  const { modal, modalOpen, handleModalClose, handleModalOpen } =
    useModalControl();

  const {
    data: discount,
    isFetching: fetchingDiscount,
    refetch: refetchDiscount,
  } = useGetDiscountQuery(
    { code: discountCode ?? "", userId: profile?._id ?? "" },
    {
      refetchOnMountOrArgChange: true,
    },
  );

  const { data: discountUsageLogs, isFetching: fetchingDiscountUsageLogs } =
    useFetchDiscountUsageLogsQuery(
      { code: discountCode ?? "", query: logsQuery },
      {
        refetchOnMountOrArgChange: true,
      },
    );

  const {
    data: events,
    isLoading: fetchingEvents,
    refetch: refetchEvents,
  } = useGetEventsQuery(
    {
      status: "upcoming",
      type: "organiser",
      currency: discount?.data?.currency ?? "NGN",
    },
    {
      refetchOnMountOrArgChange: true,
      skip: !discount?.data,
    },
  );

  const [deleteDiscount, { isLoading: deletingDiscount }] =
    useDeleteDiscountMutation();

  const [toggleAssignEventDiscount, { isLoading: assigningEventDiscount }] =
    useToggleAssignEventDiscountMutation();

  const navigate = useNavigate();

  return (
    <div className="mx-auto flex w-full max-w-[600px] flex-col gap-2">
      <BackButton
        onClick={() => {
          navigate("/discounts");
        }}
      />

      {fetchingDiscount ? (
        <BgWrapper>
          <Skeleton
            width={"100%"}
            height={252}
            className="rounded-2xl"
            baseColor={SKELETON_BASE_COLOR}
            highlightColor={SKELETON_HIGHLIGHT_COLOR}
          />
        </BgWrapper>
      ) : (
        <div className="flex flex-col gap-[2px]">
          <BgWrapper className="flex flex-wrap items-center gap-5 !py-6 md:flex-nowrap">
            <div className="w-full md:w-fit">
              <Icon
                svg="ticket-percent"
                width={56}
                height={56}
                className="rounded-full bg-lighter-blue p-4 text-blue"
              />
            </div>

            <div className="flex-1 overflow-hidden">
              <p className="text-32 line-clamp-1 block max-w-full overflow-hidden text-ellipsis font-fraunces uppercase">
                {discount?.data?.name}
              </p>
              <p className="mt-2 font-bold uppercase text-grey-600">{`${
                discount?.data?.type === "percentage"
                  ? `${discount?.data?.value}%`
                  : `${formatToCurrency(discount?.data?.value ?? 0, {
                      currency:
                        currencyShorts[discount?.data?.currency ?? "NGN"],
                    })}`
              } off ∙ ${discount?.data?.type === "percentage" ? `${discount?.data?.value === 100 ? "Free" : "Percentage Discount"}` : "Amount Discount"}`}</p>
            </div>

            <Pill
              text={discount?.data?.status === "active" ? "ACTIVE" : "INACTIVE"}
              variation={discount?.data?.status === "active" ? "green" : "grey"}
            />
          </BgWrapper>
          <BgWrapper>
            <p className="text-2xl font-medium text-grey-600">
              {discountUsageLogs?.data?.totalUsage ?? 0}/
              {discount?.data?.quantityType === "limited"
                ? discount?.data?.quantity
                : "∞"}{" "}
              Used
            </p>

            <ProgressBar
              step={discountUsageLogs?.data?.totalUsage ?? 0}
              total={
                discount?.data?.quantityType === "limited"
                  ? (discount?.data?.quantity ?? 0)
                  : (discountUsageLogs?.data?.totalUsage ?? 0)
              }
              className="mt-3"
              singleBarClassName="!bg-success"
            />

            <div className="mt-6 grid grid-cols-2 gap-[10px]">
              <Button
                className=""
                type="button"
                onClick={() => {
                  handleModalOpen("editDiscountModal");
                }}
                variation="plain-grey"
                icon="pencil"
                text="Edit"
              />

              <Button
                className=""
                type="button"
                onClick={() => {
                  handleModalOpen("deleteDiscountModal");
                }}
                variation="plain-danger"
                icon="bin"
                text="Delete"
              />
            </div>
          </BgWrapper>
        </div>
      )}

      <BgWrapper className="flex flex-col gap-3">
        <div>
          <MiniHeader
            header="Events"
            subheader="Only upcoming events would show here"
          />
        </div>

        <TableComponent
          headers={[]}
          rows={
            events?.data?.data?.map((event) => {
              return {
                id: event._id,
                items: [
                  <div className="w-fill flex items-center gap-3">
                    <div className="flex-1 text-sm">
                      <p className="font-bold text-grey-900">{event.name}</p>
                      <p className="text-grey-600">
                        Event{" "}
                        {getTimeToGo(event.startDate) === "past"
                          ? `started ${getTimeAgo(event.startDate)}`
                          : `day in ${getTimeToGo(event.startDate)}`}
                      </p>
                    </div>

                    <SwitchInput
                      checked={(
                        event?.discounts?.map((d) =>
                          typeof d === "string" ? d : d._id,
                        ) ?? []
                      ).includes(discount?.data?._id ?? "")}
                      onChange={async (checked) => {
                        const resp = await toggleAssignEventDiscount({
                          eventId: event._id,
                          discountCode: discount?.data.name ?? "",
                          discountCreatedBy: discount?.data
                            ?.createdBy as unknown as string,
                          status: checked ? "unassign" : "assign",
                        });

                        if (resp.data?.status === "success") {
                          refetchEvents();
                        }
                      }}
                      disabled={assigningEventDiscount}
                    />
                  </div>,
                ],
              };
            }) ?? []
          }
          isLoading={fetchingEvents}
          isTableEmpty={[0, undefined].includes(events?.data?.data?.length)}
          emptyTitle="Create an event"
          emptySubTitle={`Discounts can be applied to only upcoming ${currencyShorts[discount?.data?.currency ?? "NGN"]} ${currencyFlags[discount?.data?.currency ?? "NGN"]} events`}
          emptyImage="/assets/images/calendar-3d.png"
        />
      </BgWrapper>

      <BgWrapper className="flex flex-col gap-3">
        <MiniHeader header="Usage" />

        <TableComponent
          headers={[]}
          rows={
            discountUsageLogs?.data?.usageLogs?.map((log, idx) => {
              return {
                id: `${log.email}-${idx}`,
                items: [
                  <GuestCard
                    className=""
                    name={`${log?.firstName} ${log?.lastName}`}
                    email={`${log?.event?.name}`}
                    timeAgo={getTimeAgo(log?.date)}
                    showTicket={false}
                    hideEmailOnMobile={false}
                  />,
                ],
              };
            }) ?? []
          }
          isLoading={fetchingDiscountUsageLogs}
          isTableEmpty={[0, undefined].includes(
            discountUsageLogs?.data?.usageLogs?.length,
          )}
          isPaginated
          currentPage={logsQuery.page}
          totalCount={discountUsageLogs?.data?.totalCount ?? 0}
          itemsPerPage={logsQuery.limit}
          onPageChange={(page) => {
            setLogsQuery({
              ...logsQuery,
              page: page,
            });
          }}
          emptyTitle="Discount Not Used"
          emptySubTitle="Guests who buy ticket with your discount would show here"
          emptyImage="/assets/images/percent-glass.png"
        />
      </BgWrapper>

      <DiscountModal
        open={modalOpen && modal === "editDiscountModal"}
        handleClose={() => {
          handleModalClose();
        }}
        mode="edit"
        discount={discount?.data}
        onSuccess={() => {
          handleModalClose();
          refetchDiscount();
        }}
      />

      <ConfirmDeleteModal
        open={modalOpen && modal === "deleteDiscountModal"}
        handleClose={() => {
          handleModalClose();
        }}
        title={`You're about to delete "${discount?.data?.name}"`}
        subtitle="This action cannot be undone. Are you sure you want to delete this discount?"
        onProceed={async () => {
          const resp = await deleteDiscount({
            id: discount?.data._id ?? "",
          });

          if (resp.data?.status === "success") {
            navigate("/discounts");
          }
        }}
        loading={deletingDiscount}
      />
    </div>
  );
};
